import type { IRowNode } from 'ag-grid-community';
import { isNil } from 'lodash';
import type React from 'react';
import type { Fund, Portfolio } from 'venn-api';
import type { SearchMenuBar } from 'venn-components';
import { analyticsService } from 'venn-utils';
import type { RowData } from '../../../../../modals/data-uploader/types';
import { normalizeGridPath } from '../../../../../modals/data-uploader/utils/portfolioTree';

export const useMappedPortfolioSearchMenuBar = ({
  closeSearch,
  isFundIdDisabledForMapping,
  onApplyMapping,
  fundName,
  node,
}: {
  closeSearch: () => void;
  isFundIdDisabledForMapping: (fundId: string) => boolean;
  onApplyMapping: (path: number[], fund: Fund) => void;
  fundName: string | undefined;
  node: IRowNode<RowData<Portfolio>>;
}): React.ComponentProps<typeof SearchMenuBar> => {
  return {
    refreshedStyling: true,
    autofocus: true,
    defaultMenuIsOpen: true,
    privateAssetSearchMode: 'PUBLIC_ONLY',
    investmentsOnly: true,
    isOptionDisabled: (option) => {
      const fundId = option?.value?.fund?.id;
      if (!fundId) {
        // if it does not have a fund ID, it is a tag; we should allow expanding them
        return false;
      }
      return isFundIdDisabledForMapping(fundId);
    },
    customPlaceholder: `Search for an investment to replace ${!isNil(fundName) ? `'${fundName}'` : 'your investment'}`,
    optionDisabledTooltipContent: () => 'Fund already in portfolio.',
    getOptionValue: (item) => item.value?.id?.toString() ?? '',
    onBlur: closeSearch,
    onSelected: (selectedItem) => {
      if (!selectedItem?.value?.fund) {
        return;
      }
      const gridPath = node?.data?.path ?? [];
      const normalizedPath = normalizeGridPath(gridPath);
      onApplyMapping(normalizedPath, selectedItem.value.fund);
      closeSearch();
      analyticsService.navigationTriggered({
        itemType: 'search menu',
        userIntent: 'map investment',
        destinationPageTitle: 'Portfolio Multi Uploader',
      });

      analyticsService.multiPortfolioUploaderInvestmentRemapped({
        portfolioId: node?.data?.root?.id,
        portfolioName: node?.data?.root?.name,
        newInvestmentId: selectedItem.value.fund.id,
        newInvestmentName: selectedItem.value.fund.name,
        oldInvestmentId: node?.data?.node?.fund?.id,
        oldInvestmentName: node?.data?.node?.name,
      });
    },
    location: 'portfolio-multi-uploader',
  };
};
